import { useEffect, useState } from 'react';

/**
 * The site's shared "idle turn" rhythm: dwell for a randomized few seconds,
 * then step the accumulated angle once — the BreakLine center star's
 * dwell-and-turn choreography (2026-07-17), extracted 2026-07-24 so the
 * logo's spiral mark idles on the same clock. The caller interpolates the
 * step with a CSS `transition` on transform (GPU-composited everywhere).
 *
 * The angle accumulates and is never normalized: wrapping to 0–360 would
 * interpolate the short way round instead of completing the turn just made.
 * `stepDeg` must be referentially stable (module-level const) — it's an
 * effect dependency.
 */
export function useIdleTurns(
  active: boolean,
  dwellMinMs: number,
  dwellRangeMs: number,
  stepDeg: () => number,
): number {
  const [angle, setAngle] = useState(0);

  useEffect(() => {
    if (!active) return;
    let timer: ReturnType<typeof setTimeout>;
    const scheduleTurn = () => {
      timer = setTimeout(
        () => {
          setAngle((a) => a + stepDeg());
          scheduleTurn();
        },
        dwellMinMs + Math.random() * dwellRangeMs,
      );
    };
    scheduleTurn();
    return () => clearTimeout(timer);
  }, [active, dwellMinMs, dwellRangeMs, stepDeg]);

  return angle;
}
